import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import type { FunctionReference } from "convex/server";
import { api } from "@constructionautomation/backend";
import { ExternalLink, Loader2, PackageCheck, RefreshCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { PageHeader } from "@/components/layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getTableRowsPayload, type TableRowsPayload, type TableRowsPayloadRef } from "@/lib/table-rows-cache";
import { useRouteParam } from "@/lib/use-route-param";

export const Route = createFileRoute("/procurePilot/$jobId")({
  component: ProcurePilotJobPage,
});

const STATUS_OPTIONS = [
  "not_started",
  "pricing_requested",
  "pricing_received",
  "approved",
  "ordered",
  "fabrication",
  "shipped",
  "delivered",
  "installed",
] as const;

type ProcureTable = {
  _id: string;
  name: string;
  pageNumber: number;
  rowCount: number;
};

type ProcureItem = {
  _id: string;
  packageName: string;
  title: string;
  description?: string;
  manufacturer?: string;
  specSection?: string;
  csiCode?: string;
  status: (typeof STATUS_OPTIONS)[number];
  enrichmentStatus: string;
  sourceRowIndex: number;
  procurementData?: Record<string, string>;
};

type UrlCandidate = {
  _id: string;
  itemId?: string;
  url: string;
  title?: string;
  status: string;
  confidence?: number;
};

type EnrichmentRun = {
  _id: string;
  status: string;
  searchProvider?: string;
  error?: string;
  createdAt: number;
  completedAt?: number;
};

type ProcureDetails = {
  job: { _id: string; name: string; status: string };
  project: { name: string } | null;
  tables: ProcureTable[];
  items: ProcureItem[];
  urlCandidates: UrlCandidate[];
  enrichmentRuns: EnrichmentRun[];
};

type SelectedRowsByTable = Map<string, Set<number>>;

type ProcureSelectedRowInput = {
  sourceTableId: string;
  sourceTableName: string;
  sourcePageNumber: number;
  rowIndex: number;
  headers: string[];
  rowCells: string[];
  packageName?: string;
};

type ProcurePilotJobsApi = {
  getRowsPayloadRefs: FunctionReference<
    "query",
    "public",
    { jobId: string },
    Array<TableRowsPayloadRef & { tableId: string }>
  >;
  getDetails: FunctionReference<"query", "public", { jobId: string }, ProcureDetails | null>;
  refreshScheduleStatus: FunctionReference<"mutation", "public", { jobId: string }, string>;
  generateItems: FunctionReference<
    "mutation",
    "public",
    { jobId: string; selectedRows: ProcureSelectedRowInput[] },
    { operationId: string; itemCount: number }
  >;
  updateItemStatus: FunctionReference<
    "mutation",
    "public",
    { itemId: string; status: ProcureItem["status"] },
    null
  >;
};

const procurePilotApi = (api as unknown as { procurePilot: { jobs: ProcurePilotJobsApi } })
  .procurePilot.jobs;

function JobStatusBadge({ status }: { status: string }) {
  const className =
    status === "ready_for_review" || status === "completed"
      ? "border-green-500/40 bg-green-500/15 text-green-700 dark:text-green-300"
      : status === "enriching" || status === "schedule_processing"
        ? "border-blue-500/40 bg-blue-500/15 text-blue-700 dark:text-blue-300"
        : status === "failed"
          ? "border-red-500/40 bg-red-500/15 text-red-700 dark:text-red-300"
          : "border-amber-500/40 bg-amber-500/15 text-amber-700 dark:text-amber-300";
  return <Badge className={className} variant="outline">{status.replaceAll("_", " ")}</Badge>;
}

function formatCell(value: string | undefined) {
  return value?.trim() || "-";
}

function normalizeSourceFieldName(name: string) {
  return name.toLowerCase().replace(/[^a-z0-9#]/g, "");
}

function isSourceTagField(name: string) {
  return ["tag", "mark", "identifier", "itemno", "item#"].includes(normalizeSourceFieldName(name));
}

function getItemDisplayTitle(item: ProcureItem) {
  const sourceTag = Object.entries(item.procurementData ?? {}).find(
    ([key, value]) => isSourceTagField(key) && value.trim().length > 0
  );
  return sourceTag?.[1].trim() ?? item.title;
}

function useScheduleRows(jobId: string | null) {
  const { data: refs } = useQuery(
    convexQuery(procurePilotApi.getRowsPayloadRefs, jobId ? { jobId } : "skip")
  );
  const [payloadByTableId, setPayloadByTableId] = useState<Map<string, TableRowsPayload>>(new Map());

  useEffect(() => {
    let cancelled = false;
    if (!refs) {
      setPayloadByTableId(new Map());
      return () => {
        cancelled = true;
      };
    }

    async function hydrate() {
      try {
        const typedRefs = refs as Array<TableRowsPayloadRef & { tableId: string }>;
        const entries = await Promise.all(
          typedRefs.map(async (ref) => {
            const payload = await getTableRowsPayload(ref as TableRowsPayloadRef);
            return [String(ref.tableId), payload] as const;
          })
        );
        if (!cancelled) setPayloadByTableId(new Map(entries));
      } catch (error) {
        console.error(error);
        if (!cancelled) setPayloadByTableId(new Map());
      }
    }

    void hydrate();
    return () => {
      cancelled = true;
    };
  }, [refs]);

  return payloadByTableId;
}

export function ProcurePilotJobPage() {
  const jobIdParam = useRouteParam("jobId");
  const jobId = jobIdParam;
  const { data: details, isPending } = useQuery(
    convexQuery(procurePilotApi.getDetails, jobId ? { jobId } : "skip")
  );
  const procureDetails = details as ProcureDetails | null | undefined;
  const refreshScheduleStatus = useMutation(procurePilotApi.refreshScheduleStatus);
  const generateItems = useMutation(procurePilotApi.generateItems);
  const updateItemStatus = useMutation(procurePilotApi.updateItemStatus);
  const rowsByTableId = useScheduleRows(jobId ?? null);
  const [selectedRowsByTable, setSelectedRowsByTable] = useState<SelectedRowsByTable>(new Map());
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ProcureItem | null>(null);
  const [candidateItem, setCandidateItem] = useState<ProcureItem | null>(null);

  const selectedCount = useMemo(() => {
    let count = 0;
    selectedRowsByTable.forEach((rows) => {
      count += rows.size;
    });
    return count;
  }, [selectedRowsByTable]);

  if (!jobId || isPending) {
    return (
      <div className="space-y-6">
        <PageHeader title="ProcurePilot" description="Loading procurement job..." />
        <div className="flex items-center justify-center p-12">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!procureDetails) {
    return <PageHeader title="ProcurePilot job not found" description="This job may have been deleted." />;
  }

  const handleRefresh = async () => {
    try {
      await refreshScheduleStatus({ jobId });
      toast.success("Schedule status refreshed");
    } catch (error) {
      console.error(error);
      toast.error("Failed to refresh schedule status");
    }
  };

  const toggleTable = (tableId: string, checked: boolean) => {
    const payload = rowsByTableId.get(tableId);
    setSelectedRowsByTable((prev) => {
      const next = new Map(prev);
      if (!checked || !payload) {
        next.delete(tableId);
      } else {
        next.set(tableId, new Set(payload.rows.map((_, index) => index)));
      }
      return next;
    });
  };

  const toggleRow = (tableId: string, rowIndex: number, checked: boolean) => {
    setSelectedRowsByTable((prev) => {
      const next = new Map(prev);
      const rows = new Set(next.get(tableId) ?? []);
      if (checked) rows.add(rowIndex);
      else rows.delete(rowIndex);
      if (rows.size === 0) next.delete(tableId);
      else next.set(tableId, rows);
      return next;
    });
  };

  const handleGenerate = async () => {
    const selectedRows = procureDetails.tables.flatMap((table) => {
      const payload = rowsByTableId.get(String(table._id));
      const selected = selectedRowsByTable.get(String(table._id));
      if (!payload || !selected) return [];
      return [...selected].sort((a, b) => a - b).map((rowIndex) => ({
        sourceTableId: table._id,
        sourceTableName: table.name,
        sourcePageNumber: table.pageNumber,
        rowIndex,
        headers: payload.headers,
        rowCells: payload.rows[rowIndex] ?? [],
        packageName: table.name,
      }));
    });

    if (selectedRows.length === 0) {
      toast.error("Select at least one row");
      return;
    }

    setIsGenerating(true);
    try {
      await generateItems({ jobId, selectedRows });
      toast.success("Procurement item enrichment started");
      setSelectedRowsByTable(new Map());
    } catch (error) {
      console.error(error);
      toast.error("Failed to generate procurement items");
    } finally {
      setIsGenerating(false);
    }
  };

  const groupedItems = procureDetails.items.reduce((groups, item) => {
    const items = groups.get(item.packageName) ?? [];
    items.push(item);
    groups.set(item.packageName, items);
    return groups;
  }, new Map<string, ProcureItem[]>());
  const candidatesByItemId = procureDetails.urlCandidates.reduce((groups, candidate) => {
    if (!candidate.itemId) return groups;
    const candidates = groups.get(candidate.itemId) ?? [];
    candidates.push(candidate);
    groups.set(candidate.itemId, candidates);
    return groups;
  }, new Map<string, UrlCandidate[]>());
  const latestRun = procureDetails.enrichmentRuns[0];
  const providerLabel = latestRun?.searchProvider === "exa"
    ? "Exa"
    : latestRun?.searchProvider === "not_configured"
      ? "Not configured"
      : latestRun?.searchProvider ?? "Not run";

  return (
    <div className="space-y-6">
      <PageHeader
        title={procureDetails.job.name}
        description={procureDetails.project?.name ?? "ProcurePilot"}
        breadcrumbs={[{ label: "ProcurePilot", href: "/procurePilot" }, { label: procureDetails.job.name }]}
        actions={
          <div className="flex items-center gap-2">
            <JobStatusBadge status={procureDetails.job.status} />
            <Button variant="outline" onClick={handleRefresh}>
              <RefreshCcw className="size-4" />
              Refresh
            </Button>
          </div>
        }
      />

      {procureDetails.job.status === "schedule_processing" ? (
        <Card className="border-2 shadow-sm">
          <CardHeader>
            <CardTitle>Schedule Extraction In Progress</CardTitle>
            <CardDescription>
              ProcurePilot needs the selected schedule tables before row selection can begin.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={handleRefresh}>
              <RefreshCcw className="size-4" />
              Check Status
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {procureDetails.tables.length > 0 && procureDetails.job.status !== "enriching" ? (
        <Card className="border-2 shadow-sm">
          <CardHeader>
            <CardTitle>Select Schedule Rows</CardTitle>
            <CardDescription>
              Select whole tables, then deselect individual rows that should not become procurement items.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {procureDetails.tables.map((table) => {
              const payload = rowsByTableId.get(String(table._id));
              const selectedRows = selectedRowsByTable.get(String(table._id)) ?? new Set<number>();
              const allSelected = Boolean(payload && payload.rows.length > 0 && selectedRows.size === payload.rows.length);
              return (
                <div key={table._id} className="rounded-lg border-2 border-border bg-background">
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b-2 border-border bg-muted/40 p-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={allSelected}
                          disabled={!payload}
                          onCheckedChange={(checked) => toggleTable(String(table._id), checked === true)}
                        />
                        <h3 className="font-bold tracking-tight">{table.name}</h3>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Page {table.pageNumber} · {table.rowCount} rows · Package: {table.name}
                      </p>
                    </div>
                    <Badge variant="secondary">{selectedRows.size} selected</Badge>
                  </div>
                  {!payload ? (
                    <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
                      <Loader2 className="size-4 animate-spin" />
                      Loading rows...
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">Add</TableHead>
                          {payload.headers.slice(0, 5).map((header, index) => (
                            <TableHead key={`${table._id}-${index}`}>{header || `Column ${index + 1}`}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {payload.rows.map((row, rowIndex) => (
                          <TableRow key={`${table._id}-${rowIndex}`}>
                            <TableCell>
                              <Checkbox
                                checked={selectedRows.has(rowIndex)}
                                onCheckedChange={(checked) => toggleRow(String(table._id), rowIndex, checked === true)}
                              />
                            </TableCell>
                            {payload.headers.slice(0, 5).map((_, columnIndex) => (
                              <TableCell key={`${table._id}-${rowIndex}-${columnIndex}`} className="max-w-[260px] truncate text-xs">
                                {formatCell(row[columnIndex])}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              );
            })}
            <div className="flex items-center justify-between border-t-2 border-border pt-4">
              <p className="text-sm font-medium text-muted-foreground">
                {selectedCount} rows selected for ProcurePilot enrichment
              </p>
              <Button onClick={handleGenerate} disabled={selectedCount === 0 || isGenerating}>
                {isGenerating ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
                Generate Procurement Items
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {procureDetails.items.length > 0 ? (
        <Card className="border-2 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PackageCheck className="size-5 text-primary" />
              Procurement Items
            </CardTitle>
            <CardDescription>
              Generated {procureDetails.items.length} draft items from selected schedule rows. URL search provider: {providerLabel}. URL candidates found: {procureDetails.urlCandidates.length}.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {latestRun?.searchProvider === "not_configured" && !latestRun.error ? (
              <div className="rounded-lg border-2 border-amber-500/40 bg-amber-500/10 p-4 text-sm">
                <p className="font-semibold text-amber-700 dark:text-amber-300">URL discovery did not run.</p>
                <p className="mt-1 text-muted-foreground">
                  The system created draft procurement items from the selected table rows, but `EXA_API_KEY` is not configured in the worker environment, so no product/spec-sheet PDFs were searched.
                </p>
              </div>
            ) : null}
            {latestRun?.error ? (
              <div className="rounded-lg border-2 border-red-500/40 bg-red-500/10 p-4 text-sm">
                <p className="font-semibold text-red-700 dark:text-red-300">URL discovery failed.</p>
                <p className="mt-1 text-muted-foreground">{latestRun.error}</p>
              </div>
            ) : null}
            {[...groupedItems.entries()].map(([packageName, items]) => (
              <div key={packageName} className="rounded-lg border-2 border-border">
                <div className="border-b-2 border-border bg-muted/50 px-4 py-3">
                  <h3 className="font-bold tracking-tight">{packageName}</h3>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>Manufacturer</TableHead>
                      <TableHead>Spec</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>AI</TableHead>
                      <TableHead>URL Candidates</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item) => {
                      const candidates = candidatesByItemId.get(item._id) ?? [];
                      return (
                        <TableRow key={item._id}>
                          <TableCell>
                            <button
                              type="button"
                              className="text-left font-semibold text-primary hover:underline"
                              onClick={() => setSelectedItem(item)}
                            >
                              {getItemDisplayTitle(item)}
                            </button>
                            <div className="text-xs text-muted-foreground">{item.description ?? `Row ${item.sourceRowIndex + 1}`}</div>
                          </TableCell>
                          <TableCell>{item.manufacturer ?? "-"}</TableCell>
                          <TableCell>{item.specSection ?? item.csiCode ?? "-"}</TableCell>
                          <TableCell>
                            <select
                              className="rounded-md border-2 border-input bg-background px-2 py-1 text-xs font-semibold uppercase tracking-wide"
                              value={item.status}
                              onChange={(event) => {
                                void updateItemStatus({
                                  itemId: item._id,
                                  status: event.target.value as (typeof STATUS_OPTIONS)[number],
                                });
                              }}
                            >
                              {STATUS_OPTIONS.map((status) => (
                                <option key={status} value={status}>{status.replaceAll("_", " ")}</option>
                              ))}
                            </select>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{item.enrichmentStatus}</Badge>
                          </TableCell>
                          <TableCell>
                            {candidates.length > 0 ? (
                              <div className="space-y-1">
                                {candidates.slice(0, 2).map((candidate) => (
                                  <a
                                    key={candidate._id}
                                    className="flex max-w-[280px] items-center gap-1 truncate text-xs font-semibold text-primary hover:underline"
                                    href={candidate.url}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <ExternalLink className="size-3 shrink-0" />
                                    <span className="truncate">{candidate.title || candidate.url}</span>
                                  </a>
                                ))}
                                {candidates.length > 2 ? (
                                  <button
                                    type="button"
                                    className="text-[11px] font-semibold text-primary hover:underline"
                                    onClick={() => setCandidateItem(item)}
                                  >
                                    View all {candidates.length} candidates
                                  </button>
                                ) : null}
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">No candidates found</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      <Dialog open={selectedItem !== null} onOpenChange={(open) => !open && setSelectedItem(null)}>
        <DialogContent className="max-w-3xl border-2">
          <DialogHeader>
            <DialogTitle>{selectedItem ? getItemDisplayTitle(selectedItem) : "Source Row"}</DialogTitle>
            <DialogDescription>
              Full source schedule row used to create this procurement item.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-auto rounded-lg border-2 border-border">
            <Table>
              <TableBody>
                {Object.entries(selectedItem?.procurementData ?? {}).map(([key, value]) => (
                  <TableRow key={key}>
                    <TableCell className="w-56 bg-muted/50 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      {key}
                    </TableCell>
                    <TableCell className="font-medium">{formatCell(value)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={candidateItem !== null} onOpenChange={(open) => !open && setCandidateItem(null)}>
        <DialogContent className="max-w-3xl border-2">
          <DialogHeader>
            <DialogTitle>
              URL Candidates{candidateItem ? ` for ${getItemDisplayTitle(candidateItem)}` : ""}
            </DialogTitle>
            <DialogDescription>
              Candidate product, submittal, and spec-sheet URLs returned by Exa.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {(candidateItem ? candidatesByItemId.get(candidateItem._id) ?? [] : []).map((candidate, index) => (
              <a
                key={candidate._id}
                href={candidate.url}
                target="_blank"
                rel="noreferrer"
                className="block rounded-lg border-2 border-border p-3 transition-colors hover:bg-muted/50"
              >
                <div className="flex items-start gap-2 font-semibold text-primary">
                  <ExternalLink className="mt-0.5 size-4 shrink-0" />
                  <span>{candidate.title || `Candidate ${index + 1}`}</span>
                </div>
                <div className="mt-1 break-all text-xs text-muted-foreground">{candidate.url}</div>
              </a>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
